import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { Carousel } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './CategorySlider.css';
import API_BASE_URL from '../api/Config';

const Banner = ({ image, title, description, buttonLabel, productId, categoryId, categoryName, bannerId, bannerData }) => {
  const navigate = useNavigate(); 

  const handleRedirect = () => {
    if (productId) {
      navigate(`/product/${productId}`);
    } else if (categoryId && categoryName) {
      const slug = categoryName.toLowerCase().replace(/\s+/g, '-');
      navigate(`/category/${slug}`);
    } else if (categoryId) {
      navigate(`/categories`, { state: { categoryId, clearProducts: true } });
    } else {
      navigate(`/banner-products/${bannerId}`, { state: { bannerData } });
    }
  };

  return (
    <div 
      className="banner-ad-component" 
      onClick={handleRedirect} 
      onTouchEnd={handleRedirect}
      style={{ cursor: 'pointer' }}
    >
      <div className="banner-content">
        <div className="banner-image">
          <img
            src={image}
            alt={`${title} - Interior Design Services & Products Nairobi Kenya`}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x200';
            }}
          />
        </div>
        <div className="banner-text">
          <button 
            className="banner-button" 
            onClick={(e) => {
              e.stopPropagation(); 
              handleRedirect();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation(); 
              handleRedirect();
            }}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const CategorySlider = () => {
  const [banners, setBanners] = useState([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    const fetchBannersAndCategories = async () => {
      try {
        // Fetch banners and categories in parallel
        const [bannerRes, catRes] = await Promise.all([
          fetch(`${API_BASE_URL}/banners`, { headers: { Accept: 'application/json' } }),
          fetch(`${API_BASE_URL}/categories`, { headers: { Accept: 'application/json' } }),
        ]);

        if (!bannerRes.ok) throw new Error(`Banners HTTP error! status: ${bannerRes.status}`);
        if (!catRes.ok) throw new Error(`Categories HTTP error! status: ${catRes.status}`);

        const [bannerData, catData] = await Promise.all([
          bannerRes.json(),
          catRes.json(),
        ]);

        // Build a map of category id → category name
        const catMap = Object.fromEntries(catData.map((c) => [c.id, c.name]));

        const transformedBanners = bannerData
          .filter((banner) => banner.status === 1)
          .map((banner) => ({
            id: banner.id,
            title: banner.title,
            imageUrl:
              banner.image_fullpath ||
              `https://admin.techieinteriors.co.ke/storage/banner/${banner.image}`,
            productId: banner.product_id || null,
            categoryId: banner.category_id || null,
            categoryName: catMap[banner.category_id] || null, // resolved from catMap
            bannerType: banner.banner_type || 'primary',
            description: banner.description || 'Click for more details!',
          }));

        setBanners(transformedBanners);
      } catch (error) {
        console.error('Error fetching banners or categories:', error);
        setBanners([]);
      }
    };

    fetchBannersAndCategories();
  }, []);

  const prev = () => carouselRef.current?.prev();
  const next = () => carouselRef.current?.next();

  return (
    <div className="category-slider-container">
      <Carousel
        ref={carouselRef}
        effect="transition"
        autoplay
        autoplaySpeed={10000}
        dots={false}
        className="category-carousel"
        draggable={true}
        swipeToSlide={true}
        touchThreshold={5}
      >
        {banners.map((banner) => (
          <div key={banner.id}>
            <Banner
              image={banner.imageUrl}
              title={banner.title || 'No Title'}
              description="Click for more details!"
              buttonLabel={banner.productId ? 'Shop now' : 'View More'}
              productId={banner.productId}
              categoryId={banner.categoryId}
              categoryName={banner.categoryName}
              bannerId={banner.id}
              bannerData={banner}
            />
          </div>
        ))}
      </Carousel>

      <span onClick={prev} className="scroll-button left">
        <LeftOutlined />
      </span>
      <span onClick={next} className="scroll-button right">
        <RightOutlined />
      </span>
    </div>
  );
};

export default CategorySlider;